import { useState, useEffect } from 'react';
import {
	Button,
	Typography,
	Box,
	IconButton,
	Paper,
	Chip,
	CircularProgress,
	Tooltip,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	useTheme,
	useMediaQuery,
	Divider,
	Stack,
} from '@mui/material';
import {
	Timeline,
	TimelineItem,
	TimelineSeparator,
	TimelineConnector,
	TimelineDot,
	TimelineContent,
} from '@mui/lab';
import RestoreIcon from '@mui/icons-material/Restore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDistanceToNow } from 'date-fns';
import promptService from '../../services/promptService';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import { StyledDrawer } from '../common/StyledDrawer';

const PromptVersionsDrawer = ({
	open,
	onClose,
	prompt,
	tenantId,
	onVersionChange,
}) => {
	const [versions, setVersions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [selectedVersion, setSelectedVersion] = useState(null);
	const [versionContent, setVersionContent] = useState('');
	const [latestVersionNumber, setLatestVersionNumber] = useState(null);
	const [activeVersion, setActiveVersion] = useState(null);
	const [showVersions, setShowVersions] = useState(true);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	// Flag to determine if the prompt is a system prompt that can have tenant defaults
	const isSystemPrompt = prompt && prompt.isSystemPrompt;

	// Fetch prompt versions when the dialog opens
	useEffect(() => {
		const fetchVersions = async () => {
			if (!open || !prompt || !tenantId) return;

			setLoading(true);
			setError(null);

			try {
				// Fetch all versions
				const versionsResponse = await promptService.getVersions(
					tenantId,
					prompt.id
				);

				if (versionsResponse && versionsResponse.length > 0) {
					// Fetch the prompt with tenant default version info
					const promptResponse = await promptService.getPrompt(
						tenantId,
						prompt.id,
						{ includeTenantDefault: true }
					);

					// Find the current active version
					const currentVersion = versionsResponse.find(
						(v) => v.version === promptResponse.prompt_version
					);

					if (currentVersion) {
						// Fetch the content of the current version
						const versionResponse = await promptService.getVersion(
							tenantId,
							prompt.id,
							currentVersion.version
						);

						// Batch state updates that affect the UI
						Promise.resolve().then(() => {
							setSelectedVersion(currentVersion.version);

							// Set the active version based on tenant default or prompt version
							if (
								isSystemPrompt &&
								promptResponse.tenant_default_version_number
							) {
								setActiveVersion(promptResponse.tenant_default_version_number);
							} else {
								setActiveVersion(promptResponse.prompt_version);
							}

							setVersionContent(versionResponse.content || '');
						});
					}
					// Update state not critical for rendering initial component
					// (avoids initial render flash)
					setLatestVersionNumber(promptResponse.latest_version_number);
					setVersions(versionsResponse);
					setLoading(false); // After all state updates
				} else {
					setLoading(false);
				}
			} catch (err) {
				console.error('Failed to fetch prompt versions:', err);
				setError('Failed to load versions. Please try again.');
				setLoading(false);
			}
		};

		if (open) {
			fetchVersions();
		} else {
			// Reset state when dialog closes
			setVersions([]);
			setSelectedVersion(null);
			setVersionContent('');
			setLatestVersionNumber(null);
			setError(null);
		}
	}, [open, prompt, tenantId, isSystemPrompt]);

	// Function to set a version as the current/active version
	const handleSetActiveVersion = async (versionNumber) => {
		if (!prompt) {
			setError('No prompt available');
			return;
		}

		setLoading(true);
		setError(null);

		try {
			// For system prompts, we need to set the tenant's default version
			if (isSystemPrompt) {
				// Find the version with this version number to get its ID
				const versionObj = versions.find((v) => v.version === versionNumber);
				if (!versionObj) {
					throw new Error('Version not found');
				}

				// Set as tenant default version
				await promptService.setTenantDefaultVersion(
					tenantId,
					prompt.id,
					versionObj.id
				);
			} else {
				// For tenant prompts, use the regular setActiveVersion
				await promptService.setActiveVersion(
					tenantId,
					prompt.id,
					versionNumber
				);
			}

			// Refresh the prompt if callback provided
			if (onVersionChange) {
				const updatedPrompt = await promptService.getPrompt(
					tenantId,
					prompt.id,
					{ includeTenantDefault: true }
				);
				onVersionChange(updatedPrompt);
			}

			// Update the activeVersion state to reflect the change
			setActiveVersion(versionNumber);
			handleClose();
		} catch (err) {
			console.error('Failed to set active version:', err);
			setError('Failed to set active version. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Function to view a specific version's content
	const handleViewVersion = async (versionNumber) => {
		if (selectedVersion === versionNumber || !prompt) return;
		setError(null);

		try {
			const response = await promptService.getVersion(
				tenantId,
				prompt.id,
				versionNumber
			);

			if (response.content) {
				setVersionContent(response.content);
				setSelectedVersion(versionNumber);
			}
		} catch (err) {
			console.error('Failed to fetch version content:', err);
			setError('Failed to load version content. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Function to use latest version (works for both system and tenant prompts)
	const handleUseLatest = async () => {
		if (!prompt || !latestVersionNumber) {
			setError('No prompt or latest version available');
			return;
		}

		setLoading(true);
		setError(null);

		try {
			if (isSystemPrompt) {
				// For system prompts, clear the tenant default to use the global latest
				await promptService.setTenantDefaultVersion(tenantId, prompt.id);
			} else {
				// For tenant prompts, set the active version to the latest
				await promptService.setActiveVersion(
					tenantId,
					prompt.id,
					latestVersionNumber
				);
			}

			// Refresh the prompt if callback provided
			if (onVersionChange) {
				const updatedPrompt = await promptService.getPrompt(
					tenantId,
					prompt.id,
					{ includeTenantDefault: true }
				);
				onVersionChange(updatedPrompt);
			}

			// Set the active version to latest for both system and tenant prompts
			setActiveVersion(latestVersionNumber);
		} catch (err) {
			console.error('Failed to use latest version:', err);
			setError('Failed to use latest version. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// Function to fork prompt with specific version
	const handleForkPrompt = async (versionNumber) => {
		if (!prompt) {
			setError('No prompt available');
			return;
		}

		setLoading(true);
		setError(null);

		try {
			// Call the fork API
			const response = await promptService.forkPrompt(
				tenantId,
				prompt.id,
				versionNumber
			);

			// Notify parent of the fork with a special action type
			if (onVersionChange) {
				// Pass 'fork' as the action type so the parent component knows this is a fork operation
				onVersionChange(response, 'fork');
			}

			handleClose();
		} catch (err) {
			console.error('Failed to fork prompt:', err);
			setError('Failed to fork prompt. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		// Reset state before closing
		setVersions([]);
		setSelectedVersion(null);
		setVersionContent('');
		setLatestVersionNumber(null);
		setError(null);

		// Call the onClose callback
		if (onClose) {
			onClose();
		}
	};

	const renderVersionList = () => (
		<Timeline
			position='right'
			sx={{
				p: 0,
				m: 0,
				// set line flush on left edge
				[`& .MuiTimelineItem-root.MuiTimelineItem-missingOppositeContent`]: {
					'&::before': { display: 'none' },
					marginLeft: 0,
					paddingLeft: 0,
				},
			}}
		>
			{versions.map((version, index) => {
				const isCurrent = version.version === activeVersion;

				return (
					<TimelineItem key={version.version} sx={{ px: 0 }}>
						{/* Line and Dot */}
						<TimelineSeparator>
							<TimelineDot
								color={isCurrent ? 'primary' : 'grey'}
								variant={isCurrent ? 'filled' : 'outlined'}
							/>
							{index < versions.length - 1 && <TimelineConnector />}
						</TimelineSeparator>

						{/* Main Content Area */}
						<TimelineContent sx={{ py: 1, flex: 1, pl: 1, pr: 0 }}>
							<Box
								onClick={() => handleViewVersion(version.version)}
								sx={{
									p: 1,
									borderRadius: 1,
									bgcolor:
										selectedVersion === version.version
											? 'action.selected'
											: 'transparent',
									cursor: 'pointer',
									transition: 'background-color 0.2s ease',
									'&:hover': { backgroundColor: 'action.hover' },
								}}
							>
								{isCurrent && (
									<Chip
										size='small'
										label='Current'
										color='primary'
										sx={{ mb: 1 }}
									/>
								)}

								{/* Version Name + Actions */}
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
									}}
								>
									<Typography variant='body2' fontWeight={500}>
										Version {version.version}
									</Typography>

									{/* Icon Buttons */}
									<Stack direction='row'>
										<Tooltip title='Set as Current Version'>
											<span>
												<IconButton
													size='small'
													onClick={(e) => {
														e.stopPropagation();
														handleSetActiveVersion(version.version);
													}}
													disabled={loading || isCurrent}
													sx={{ pt: 0, pr: 0.25, mt: 0 }}
												>
													<RestoreIcon fontSize='small' />
												</IconButton>
											</span>
										</Tooltip>

										{isSystemPrompt && (
											<Tooltip title='Copy as New Prompt'>
												<span>
													<IconButton
														size='small'
														onClick={(e) => {
															e.stopPropagation();
															handleForkPrompt(version.version);
														}}
														disabled={loading}
														sx={{ pt: 0, mt: 0 }}
													>
														<ContentCopyIcon fontSize='small' />
													</IconButton>
												</span>
											</Tooltip>
										)}
									</Stack>
								</Box>

								{/* Timestamp */}
								<Typography variant='caption' color='text.secondary'>
									{version.created_at
										? `Created ${formatDistanceToNow(new Date(version.created_at))} ago`
										: 'Recently created'}
								</Typography>
							</Box>
						</TimelineContent>
					</TimelineItem>
				);
			})}
		</Timeline>
	);

	return (
		<StyledDrawer
			anchor={isMobile ? 'bottom' : 'right'}
			open={open}
			onClose={onClose}
			isMobile={isMobile}
			maxWidth={'700px'}
		>
			{/* Header */}
			<Box
				sx={{
					borderBottom: `1px solid ${theme.palette.divider}`,
					p: 2,
				}}
			>
				<Box
					display='flex'
					alignItems='center'
					justifyContent='space-between'
					sx={{ m: 0, p: 0 }}
				>
					<Typography variant='h6' lineHeight='1.2'>
						{prompt?.name} - Version History
					</Typography>
					<IconButton onClick={onClose} edge='end' aria-label='close'>
						<CloseIcon />
					</IconButton>
				</Box>
				{!loading && prompt && activeVersion !== latestVersionNumber && (
					<>
						<Tooltip title='Use Latest Version'>
							<Button
								size='small'
								color='primary'
								startIcon={<NewReleasesIcon />}
								onClick={handleUseLatest}
								disabled={loading || activeVersion === latestVersionNumber}
								sx={{ p: 0.5, mt: 0 }}
							>
								Use Latest
							</Button>
						</Tooltip>

						{prompt.hasNewerVersion && (
							<Chip
								icon={<NewReleasesIcon />}
								label={`Newer version available (v${prompt.latestVersion})`}
								color='warning'
								size='small'
								sx={{ mx: 2 }}
							/>
						)}
					</>
				)}
			</Box>

			<Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, width: '100%' }}>
				{loading && versions.length === 0 ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
						<CircularProgress />
					</Box>
				) : (
					<Box sx={{ display: isMobile ? 'block' : 'flex', gap: 1 }}>
						{isMobile ? (
							// Accordion for small screens
							<Accordion
								expanded={showVersions}
								onChange={() => setShowVersions(!showVersions)}
								sx={{ mb: 2 }}
							>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography variant='subtitle1' sx={{ mb: 0 }}>
										Available Versions
									</Typography>
								</AccordionSummary>
								<AccordionDetails sx={{ overflowY: 'auto' }}>
									{renderVersionList()}
								</AccordionDetails>
							</Accordion>
						) : (
							<Box sx={{ minWidth: '220px' }}>
								<Typography variant='subtitle1' gutterBottom>
									Available Versions
								</Typography>
								{renderVersionList()}
							</Box>
						)}

						{!isMobile && !loading && (
							<Divider
								orientation='vertical'
								flexItem
								sx={{
									mx: 1,
									borderColor: theme.palette.divider,
									height: 'auto',
									alignSelf: 'stretch',
								}}
							/>
						)}

						<Box sx={{ width: isMobile ? '100%' : '70%' }}>
							{error && (
								<Typography color='error' sx={{ mb: 2 }}>
									{error}
								</Typography>
							)}

							{!loading && (
								<Typography variant='subtitle1' gutterBottom>
									{selectedVersion
										? `Content for Version ${selectedVersion}`
										: 'Select a version'}
								</Typography>
							)}

							{loading && selectedVersion ? (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
									}}
								>
									<CircularProgress />
								</Box>
							) : (
								<Paper
									elevation={0}
									sx={{
										p: 2,
										backgroundColor: '#f5f5f5',
										minHeight: '300px',
										whiteSpace: 'pre-wrap',
										minWidth: '300px',
									}}
								>
									<Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
										{versionContent ? versionContent : 'No content to display'}
									</Typography>
								</Paper>
							)}
						</Box>
					</Box>
				)}
			</Box>
		</StyledDrawer>
	);
};

PromptVersionsDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	prompt: PropTypes.object,
	tenantId: PropTypes.string.isRequired,
	onVersionChange: PropTypes.func,
};

export default PromptVersionsDrawer;
